import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './NotFound.module.css';

/**
 * Where a URL that goes nowhere lands.
 *
 * The catch-all used to redirect silently to `/`, which is disorienting: a
 * mistyped or stale link dumped you somewhere else with no explanation, and a
 * bookmark that had moved looked like a login loop.
 *
 * The metaphor is deliberate. Suspire's subject is growing things sustainably,
 * so a seed that has not sprouted yet says "nothing here" without implying
 * anything broke — which is both kinder and more accurate than a crash. It also
 * gives the page somewhere warm to point next.
 */

/**
 * One is picked per visit. Sustainability lines, chosen because they land on
 * patience and small beginnings rather than doom — the register we want when
 * someone has just hit a dead end.
 */
const QUOTES = [
  {
    text: 'The best time to plant a tree was twenty years ago. The second best time is now.',
    who: 'Proverb',
  },
  {
    text: 'We do not inherit the earth from our ancestors; we borrow it from our children.',
    who: 'Native American proverb',
  },
  {
    text: 'The greatest threat to our planet is the belief that someone else will save it.',
    who: 'Robert Swan',
  },
  {
    text: 'What we are doing to the forests of the world is but a mirror reflection of what we are doing to ourselves.',
    who: 'Mahatma Gandhi',
  },
  {
    text: 'Never doubt that a small group of thoughtful, committed citizens can change the world.',
    who: 'Margaret Mead',
  },
  {
    text: 'He that plants trees loves others besides himself.',
    who: 'Thomas Fuller',
  },
];

/** Light on the wordplay, heavy on the reassurance. */
const HEADLINES = [
  'This one never sprouted',
  'Nothing growing here yet',
  'This patch is bare',
];

function pick(list) {
  return list[Math.floor(Math.random() * list.length)];
}

/** Slow-drifting motes, generated once so they do not jump on re-render. */
function useMotes(count = 18) {
  return React.useMemo(
    () =>
      Array.from({ length: count }, (_, i) => ({
        id: i,
        left: `${Math.random() * 100}%`,
        size: `${2 + Math.random() * 3}px`,
        duration: `${14 + Math.random() * 16}s`,
        delay: `${Math.random() * 14}s`,
      })),
    [count]
  );
}

function NotFound({ onMount }) {
  const navigate = useNavigate();
  const location = useLocation();
  const motes = useMotes();

  // Tells the shell to stand down while this is on screen.
  //
  // The routes are partly generated from FEATURE_PAGE_MAP, so App cannot know
  // whether a path matched without duplicating the table — and a duplicate list
  // would drift the first time a feature is added. Letting the page that only
  // renders on a miss announce itself keeps the knowledge in one place.
  React.useEffect(() => {
    if (!onMount) return undefined;
    onMount(true);
    return () => onMount(false);
  }, [onMount]);

  const quote = React.useMemo(() => pick(QUOTES), []);
  const headline = React.useMemo(() => pick(HEADLINES), []);

  return (
    <div className={styles.page}>
      {motes.map((m) => (
        <span
          key={m.id}
          className={styles.mote}
          style={{
            left: m.left,
            width: m.size,
            height: m.size,
            animationDuration: m.duration,
            animationDelay: m.delay,
          }}
          aria-hidden="true"
        />
      ))}

      <div className={styles.content}>
        <svg
          className={styles.art}
          viewBox="0 0 200 200"
          fill="none"
          role="img"
          aria-label="A seedling in soil"
        >
          {/* Soil */}
          <ellipse cx="100" cy="170" rx="62" ry="12" fill="#134e4a" opacity="0.55" />
          <path d="M42 170 Q100 152 158 170 L158 178 Q100 190 42 178 Z" fill="#1e293b" />

          <g className={styles.sprout}>
            {/* Stem */}
            <path
              d="M100 168 C100 148 98 132 100 112"
              stroke="#0d9488"
              strokeWidth="4"
              strokeLinecap="round"
            />
            {/* Two leaves, one a little larger — deliberately not symmetrical */}
            <path
              d="M100 128 C82 124 70 110 72 96 C88 94 100 106 100 128 Z"
              fill="#14b8a6"
              opacity="0.92"
            />
            <path
              d="M100 118 C120 112 134 96 131 80 C112 79 100 94 100 118 Z"
              fill="#5eead4"
              opacity="0.85"
            />
          </g>

          {/* The seed still under the soil, waiting */}
          <circle cx="100" cy="176" r="4.5" fill="#0f766e" opacity="0.9" />
        </svg>

        <p className={styles.code}>404</p>
        <h1 className={styles.title}>{headline}</h1>
        <p className={styles.body}>
          The page you were after has moved, been renamed, or never existed. Nothing
          is broken — the rest of Suspire is right where you left it.
        </p>

        <blockquote className={styles.quote}>
          “{quote.text}”
          <cite className={styles.attribution}>— {quote.who}</cite>
        </blockquote>

        <div className={styles.actions}>
          <button type="button" className={styles.primary} onClick={() => navigate('/')}>
            Take me home
          </button>
          <button type="button" className={styles.secondary} onClick={() => navigate(-1)}>
            ← Go back
          </button>
        </div>

        {/* The URL, so a mistyped link is obvious and a bad link is reportable. */}
        <p className={styles.path}>{location.pathname}</p>
      </div>
    </div>
  );
}

export default NotFound;
